import * as yup from 'yup';
import * as formSchemaUtils from './formSchemaUtils';
import { CONTACT_BATCH_MODE, fieldKey } from '../constants';

const getContactsSchemaForMode = (mode) => {
  if (mode === CONTACT_BATCH_MODE.REMOVE) {
    return {
      contacts: yup.mixed().test(
        'remove-min',
        'batch.update.contacts.error.remove.min',
        (value) => Array.isArray(value) && value.length >= 1
      )
    };
  }
  if (mode === CONTACT_BATCH_MODE.REPLACE) {
    return {
      contacts: yup.mixed()
        .test(
          'replace-to-required',
          'batch.update.contacts.error.replace.to.required',
          (value) => Array.isArray(value) && value.length === 1 && value[0] && value[0].person && value[0].person.id
        )
        .test(
          'replace-to-not-ex-cern',
          'batch.update.contacts.replace.to.not.at.cern.error',
          (value) => !Array.isArray(value) || !value.some(v => v && v.person && v.person.atCern === 'N')
        ),
      replaceFromPerson: yup.object().nullable()
        .required('batch.update.contacts.error.replace.from.required')
        .test('not-equal', 'batch.update.contacts.error.replace.from.equals.to', function (value) {
          const to = (this.parent.contacts || [])[0];
          return !value || !to || !to.person || value.id !== to.person.id;
        })
    };
  }
  // ADD or OVERRIDE — list must be non-empty (legacy behaviour).
  // Both reject ex-CERN people, since both establish a forward-looking contact
  // list on the inventory and an ex-CERN person can't act as a contact.
  const baseContacts = yup.mixed().test('contacts', formSchemaUtils.LIST_CANNOT_BE_EMPTY, value => {
    return (
      value &&
      value.length > 0 &&
      value.filter(
        v => !v['wasDeleted'] && (!v['delDte'] || new Date(v['delDte']) > new Date())
      ).length > 0
    );
  });
  if (mode === CONTACT_BATCH_MODE.ADD || mode === CONTACT_BATCH_MODE.OVERRIDE) {
    return {
      contacts: baseContacts.test(
        'forward-no-ex-cern',
        'batch.update.contacts.forward.not.at.cern.error',
        value => Array.isArray(value) && !value.some(v => v && v.person && v.person.atCern === 'N')
      )
    };
  }
  return { contacts: baseContacts };
};

const getBatchUpdateFormSchema = (selectedFields, contactBatchMode) => {
  let selectedFieldsList = Object.keys(selectedFields).map(entry => selectedFields[entry].key);

  let envAttrsDynamicRequired = {};
  Object.keys(selectedFieldsList).forEach(k => {
    if (fieldKey.envAttrs[selectedFieldsList[k]] !== undefined) {
      Object.assign(envAttrsDynamicRequired, {
        [fieldKey.envAttrs[selectedFieldsList[k]]]: formSchemaUtils.objectWithIdRequired()
      });
    }
  });

  return yup.object().shape({
    ...(selectedFieldsList.includes(fieldKey.GEN_LOCATION_NAME) && {
      outOfBuilding: yup
        .mixed()
        .outOfBuildingRequiredValidation(yup.ref('location.locationInfo'))
        .outOfBuildingErrorValidation(yup.ref('location.locationInfo'))
        .nullable(),
      location: formSchemaUtils.objectRequired()
    }),
    ...(selectedFieldsList.includes(fieldKey.GEN_CONTACTS) && getContactsSchemaForMode(contactBatchMode)),

    ...(selectedFieldsList.includes(fieldKey.GEN_QUANTITY) && {
      quantity: yup
        .number()
        .min(0, formSchemaUtils.VALUE_MUST_BE_POSITIVE_INTEGER)
        .max(999999999999999999, formSchemaUtils.VALUE_EXCEEDS_MAX)
        .required(formSchemaUtils.VALUE_IS_REQUIRED)
        .nullable()
    }),

    ...(selectedFieldsList.includes(fieldKey.GEN_TRADE_NAME) && {
      chemicalProduct: yup.object().shape({
        selectedChemicalProduct: formSchemaUtils.objectWithIdRequired()
      })
    }),
    ...(selectedFieldsList.some(r => Object.keys(fieldKey.envAttrs).includes(r)) && {
      envAttributes: yup.object(envAttrsDynamicRequired)
    })
  });
};

export default getBatchUpdateFormSchema;
